import { createContext, useContext, useState, useEffect } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context for reCAPTCHA configuration
const ReCaptchaContext = createContext({
  enabled: false,
  siteKey: null,
  loading: true
});

export const useReCaptchaConfig = () => useContext(ReCaptchaContext);

// Hook to execute reCAPTCHA
export const useReCaptchaAction = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { enabled } = useReCaptchaConfig();

  const executeAction = async (action) => {
    if (!enabled || !executeRecaptcha) {
      return null; // reCAPTCHA not enabled or not ready
    }
    
    try {
      const token = await executeRecaptcha(action);
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  };

  return { executeAction, enabled };
};

// Provider component that fetches config and wraps children
export const ReCaptchaConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    enabled: false,
    siteKey: null,
    loading: true
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config/recaptcha`);
        setConfig({
          enabled: response.data.enabled,
          siteKey: response.data.siteKey,
          loading: false
        });
      } catch (error) {
        console.error('Failed to fetch reCAPTCHA config:', error);
        setConfig({
          enabled: false,
          siteKey: null,
          loading: false
        });
      }
    };

    fetchConfig();
  }, []);

  if (config.loading) {
    return (
      <ReCaptchaContext.Provider value={config}>
        {children}
      </ReCaptchaContext.Provider>
    );
  }

  // If reCAPTCHA is enabled, wrap with Google provider
  if (config.enabled && config.siteKey) {
    return (
      <ReCaptchaContext.Provider value={config}>
        <GoogleReCaptchaProvider
          reCaptchaKey={config.siteKey}
          scriptProps={{
            async: true,
            defer: true,
            appendTo: 'head'
          }}
        >
          {children}
        </GoogleReCaptchaProvider>
      </ReCaptchaContext.Provider>
    );
  }

  // If not enabled, just provide context without Google wrapper
  return (
    <ReCaptchaContext.Provider value={config}>
      {children}
    </ReCaptchaContext.Provider>
  );
};

export default ReCaptchaConfigProvider;
